import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Copy } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, fmt, genPassword } from "../client";
import { PageHeader, Panel, Pill, Field } from "../ui";

const ROLES = ["support_tier_1", "support_tier_2", "super_admin"];
const MASTER = "joe.shaferjr@gmail.com";

const TempPassword = ({ value }) => (
  <div className="flex items-center gap-2 rounded-lg border border-[var(--s-accent)]/50 bg-[var(--s-accent)]/10 p-3" data-testid="team-temp-password-result">
    <code className="s-mono flex-1 break-all text-[var(--s-accent)]">{value}</code>
    <button onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }} data-testid="team-copy-temp-password"><Copy size={16} /></button>
  </div>
);

const AddEmployee = ({ open, onClose, onDone }) => {
  const [f, setF] = useState({ display_name: "", email: "", role: "support_tier_1", temp_password: genPassword() });
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api("/staff/team", { method: "POST", body: f });
      setIssued(r.temp_password); onDone(); toast.success("Employee provisioned");
    } catch (e2) { toast.error(e2.message); } finally { setBusy(false); }
  };
  const close = () => { setIssued(""); setF({ display_name: "", email: "", role: "support_tier_1", temp_password: genPassword() }); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="staff-root border-[var(--s-line)] bg-[var(--s-panel)]" data-testid="team-add-modal">
        <DialogHeader><DialogTitle className="s-display">Add employee</DialogTitle></DialogHeader>
        {issued ? (
          <div className="space-y-4">
            <p className="text-sm text-[var(--s-dim)]">Share this temporary password securely. They must change it at first sign-in.</p>
            <TempPassword value={issued} />
            <Button className="s-btn w-full" onClick={close} data-testid="team-add-done">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <Field label="Staff display name"><Input required value={f.display_name} onChange={set("display_name")} className="s-input" data-testid="team-add-name" /></Field>
            <Field label="Staff email"><Input required type="email" value={f.email} onChange={set("email")} className="s-input" data-testid="team-add-email" /></Field>
            <Field label="Assigned role">
              <Select value={f.role} onValueChange={set("role")}>
                <SelectTrigger className="s-input" data-testid="team-add-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} data-testid={`team-role-${r}`}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Generated temporary password">
              <div className="flex gap-2">
                <Input value={f.temp_password} onChange={set("temp_password")} className="s-input s-mono" data-testid="team-add-temp-password" />
                <Button type="button" variant="outline" className="s-ghost" onClick={() => setF({ ...f, temp_password: genPassword() })} data-testid="team-add-regenerate">New</Button>
              </div>
            </Field>
            <Button type="submit" disabled={busy} className="s-btn w-full" data-testid="team-add-submit">{busy ? "Provisioning…" : "Create account"}</Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Team() {
  const [rows, setRows] = useState([]);
  const [add, setAdd] = useState(false);
  const [issued, setIssued] = useState(null);
  const load = useCallback(() => { api("/staff/team").then(setRows).catch((e) => toast.error(e.message)); }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (s, action) => {
    if (action === "deactivate" && !window.confirm(`Deactivate ${s.email}? All sessions will be revoked.`)) return;
    try {
      const r = await api(`/staff/team/${s.id}/${action}`, { method: "POST" });
      if (r.temp_password) setIssued({ email: s.email, pw: r.temp_password });
      toast.success(action === "deactivate" ? "Account deactivated" : "Temporary password reset");
      load();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <div>
      <PageHeader kicker="Master admin" title="Team roster">
        <Button className="s-btn" onClick={() => setAdd(true)} data-testid="team-add-btn"><UserPlus size={16} className="mr-2" />Add employee</Button>
      </PageHeader>
      {issued && <div className="mb-6 space-y-2"><p className="text-sm text-[var(--s-dim)]">New temporary password for {issued.email}:</p><TempPassword value={issued.pw} /></div>}
      <Panel className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="team-table">
          <thead className="s-mono text-left text-[11px] uppercase tracking-wider text-[var(--s-dim)]">
            <tr>{["Name", "Role", "Status", "Last login", "Password", ""].map((h) => <th key={h} className="px-5 py-3 font-normal">{h}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[var(--s-line)]">
            {rows.map((s) => (
              <tr key={s.id} data-testid={`team-row-${s.email}`} className="transition-colors hover:bg-white/[0.02]">
                <td className="px-5 py-4"><p>{s.display_name}</p><p className="text-xs text-[var(--s-dim)]">{s.email}</p></td>
                <td className="px-5 py-4"><span className="s-mono text-xs">{s.role}</span></td>
                <td className="px-5 py-4"><Pill tone={s.is_active ? "ok" : "bad"}>{s.is_active ? "Active" : "Deactivated"}</Pill></td>
                <td className="s-mono px-5 py-4 text-xs text-[var(--s-dim)]">{fmt(s.last_login_at)}</td>
                <td className="px-5 py-4"><Pill tone={s.must_reset_password ? "warn" : "dim"}>{s.must_reset_password ? "Temporary" : "Set"}</Pill></td>
                <td className="px-5 py-4 text-right">
                  {s.email.toLowerCase() === MASTER ? <Pill tone="warn">Master · irrevocable</Pill> : s.is_active && (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" className="s-ghost" onClick={() => act(s, "reset-temp-password")} data-testid={`team-reset-${s.email}`}>Reset temp password</Button>
                      <Button size="sm" variant="outline" className="s-danger" onClick={() => act(s, "deactivate")} data-testid={`team-deactivate-${s.email}`}>Deactivate</Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <AddEmployee open={add} onClose={() => setAdd(false)} onDone={load} />
    </div>
  );
}
